import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ESGScoreDisplay } from '../../Badge';
import {
  BiodiversityIcon,
  ClimateIcon,
  EnergyIcon,
  WasteIcon,
  WaterIcon,
  AccessibilityIcon,
  CommunityIcon,
  HumanRightsIcon,
  SourcingIcon,
  AuditabilityIcon,
  TrainingIcon,
  StrategyIcon,
} from '../../../../assets/icons';
import SelectDropdown from '../../SelectDropdown';
import { useHotelDetail } from '../../../../contexts/HotelDetailContext';
import { useBenchmarkReport } from '../../../../hooks/useBenchmarkReport';
import Loader from '../../../Loader';
import { Tooltip as UiTooltip } from '../../Tooltip';
import { Button } from '../../Button';
import { downloadPDF } from '../../../../utils';
import { API_ENDPOINTS } from '../../../../services/apiEndpoints';
import { useToast } from '../../../../hooks';
import { Banner } from '../../Banner';
// Category metadata with icons
const CATEGORY_METADATA = [
  // Planet — alphabetical
  {
    name: 'Biodiversity',
    key: 'biodiversity',
    icon: BiodiversityIcon,
    color: 'var(--color-planet-green)',
  },
  {
    name: 'Climate Action',
    key: 'climate_action',
    icon: ClimateIcon,
    color: 'var(--color-planet-green)',
  },
  {
    name: 'Energy',
    key: 'energy',
    icon: EnergyIcon,
    color: 'var(--color-planet-green)',
  },
  {
    name: 'Waste',
    key: 'waste',
    icon: WasteIcon,
    color: 'var(--color-planet-green)',
  },
  {
    name: 'Water Stewardship',
    key: 'water_stewardship',
    icon: WaterIcon,
    color: 'var(--color-planet-green)',
  },
  // People — alphabetical
  {
    name: 'Accessibility',
    key: 'accessibility',
    icon: AccessibilityIcon,
    color: 'var(--color-people-blue)',
  },
  {
    name: 'Community & Cultural Stewardship',
    key: 'community_cultural_stewardship',
    icon: CommunityIcon,
    color: 'var(--color-people-blue)',
  },
  {
    name: 'Human Rights, Justice & Wellbeing',
    key: 'human_rights_justice_wellbeing',
    icon: HumanRightsIcon,
    color: 'var(--color-people-blue)',
  },
  {
    name: 'Responsible Sourcing',
    key: 'responsible_sourcing',
    icon: SourcingIcon,
    color: 'var(--color-people-blue)',
  },
  // Policy — alphabetical
  {
    name: 'Auditability',
    key: 'auditability',
    icon: AuditabilityIcon,
    color: 'var(--color-policy-orange)',
  },
  {
    name: 'Strategy & Management',
    key: 'strategy_management',
    icon: StrategyIcon,
    color: 'var(--color-policy-orange)',
  },
  {
    name: 'Training & Engagement',
    key: 'training_engagement',
    icon: TrainingIcon,
    color: 'var(--color-policy-orange)',
  },
];
const Colors = {
  'Top Scoring Hotel': '#4B5563',
  Average: '#d1d6e1',
  'My Hotel': '#b474d2',
};

export default function Benchmark() {
  const [selectedMetric, setSelectedMetric] = useState<
    'overall' | 'marketWise' | 'brandWise'
  >('overall');
  const [selectedBrandIndex, setSelectedBrandIndex] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const { hotel } = useHotelDetail();
  const { benchmarkData, loading } = useBenchmarkReport(hotel?.id || '');
  const { success, error } = useToast();

  // Map backend data structure to component format
  const getMetricData = useMemo(() => {
    if (!benchmarkData) return null;

    const mapBenchmarkToMetric = (benchmarks: any) => {
      const result: any = {};

      // Map score_alo
      if (benchmarks.score_alo) {
        result.alo_score = {
          current: benchmarks.score_alo.hotel_score,
          benchmark: benchmarks.score_alo.topValue,
          average: benchmarks.score_alo.average,
          variance:
            benchmarks.score_alo.hotel_score - benchmarks.score_alo.average,
          topHotelName:
            benchmarks.score_alo.topHotelName ||
            benchmarks.score_alo.hotelName ||
            'Top Scoring Hotel',
        };
      }

      // Map categories
      const categoryMap: Record<string, string> = {
        Biodiversity: 'biodiversity',
        'Climate Action': 'climate_action',
        Energy: 'energy',
        Waste: 'waste',
        'Water Stewardship': 'water_stewardship',
        Accessibility: 'accessibility',
        'Community & Cultural Stewardship': 'community_cultural_stewardship',
        'Human Rights, Justice & Wellbeing': 'human_rights_justice_wellbeing',
        'Responsible Sourcing': 'responsible_sourcing',
        Auditability: 'auditability',
        'Strategy & Management': 'strategy_management',

        'Training & Engagement': 'training_engagement',
      };

      Object.entries(categoryMap).forEach(([backendKey, componentKey]) => {
        if (benchmarks[backendKey]) {
          result[componentKey] = {
            current: benchmarks[backendKey].hotel_score,
            benchmark: benchmarks[backendKey].topValue,
            average: benchmarks[backendKey].average,
            variance:
              benchmarks[backendKey].hotel_score -
              benchmarks[backendKey].average,
            topHotelName:
              benchmarks[backendKey].topHotelName ||
              benchmarks[backendKey].hotelName ||
              'Top Scoring Hotel',
          };
        }
      });

      return result;
    };

    return {
      overall: benchmarkData.overall?.benchmarks
        ? mapBenchmarkToMetric(benchmarkData.overall.benchmarks)
        : null,
      marketWise: benchmarkData.market?.benchmarks
        ? mapBenchmarkToMetric(benchmarkData.market.benchmarks)
        : null,
      brandWise:
        benchmarkData.brand?.map(brand => ({
          brandId: brand.brandId,
          brandName: brand.brandName,
          data: mapBenchmarkToMetric(brand.benchmarks),
        })) || [],
    };
  }, [benchmarkData]);

  // Get brand options for dropdown
  const brandOptions = useMemo(() => {
    if (!getMetricData?.brandWise || getMetricData.brandWise.length === 0)
      return [];
    return getMetricData.brandWise.map((brand: any, index: number) => ({
      label: brand.brandName,
      value: index.toString(),
    }));
  }, [getMetricData]);

  // Reset brand selection when switching away from brandWise
  useEffect(() => {
    if (selectedMetric !== 'brandWise') {
      setSelectedBrandIndex(0);
    }
  }, [selectedMetric]);

  const metricOptions = useMemo(() => {
    const isBrandWiseDisabled =
      !getMetricData?.brandWise || getMetricData.brandWise.length === 0;

    return [
      { label: 'Overall', value: 'overall' },
      { label: 'Market', value: 'marketWise' },
      {
        label: 'Brand',
        value: 'brandWise',
        isDisabled: isBrandWiseDisabled,
        reason: isBrandWiseDisabled
          ? 'Brand comparison not available for independent hotels'
          : undefined,
      },
    ];
  }, [getMetricData]);

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number = 20) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Prepare data for the overall score bar chart (left side)
  const overallScoreData = useMemo(() => {
    if (!getMetricData) return [];

    let metricData;
    if (
      selectedMetric === 'brandWise' &&
      getMetricData.brandWise?.[selectedBrandIndex]
    ) {
      metricData = getMetricData.brandWise[selectedBrandIndex].data.alo_score;
    } else {
      metricData = getMetricData[selectedMetric]?.alo_score;
    }

    if (!metricData) return [];

    const hotelName = hotel?.hotel_location_name || 'My Hotel';

    // Helper to safely handle null/undefined values
    const safeValue = (val: number | null | undefined) => val ?? 0;
    const safeDisplayValue = (val: number | null | undefined) => {
      const actualValue = val ?? 0;
      return actualValue === 0 ? 1 : actualValue;
    };

    return [
      {
        name: hotelName,
        value: safeValue(metricData.current),
        displayValue: safeDisplayValue(metricData.current),
        color: Colors['My Hotel'],
      },
      {
        name: 'Average',
        value: safeValue(metricData.average),
        displayValue: safeDisplayValue(metricData.average),
        color: Colors['Average'],
      },
      {
        name: 'Top Scoring Hotel',
        value: metricData.benchmark,
        displayValue: metricData.benchmark,
        color: Colors['Top Scoring Hotel'],
      },
    ];
  }, [
    selectedMetric,
    selectedBrandIndex,
    getMetricData,
    hotel?.hotel_location_name,
  ]);

  // Prepare data for category comparison (right side) - Horizontal bars
  const categoryComparisonData = useMemo(() => {
    if (!getMetricData) return [];

    let metricData;
    if (
      selectedMetric === 'brandWise' &&
      getMetricData.brandWise?.[selectedBrandIndex]
    ) {
      metricData = getMetricData.brandWise[selectedBrandIndex].data;
    } else {
      metricData = getMetricData[selectedMetric];
    }

    if (!metricData) return [];

    const hotelName = hotel?.hotel_location_name || 'My Hotel';

    // Helper to safely handle null/undefined values for categories
    const safeCategoryValue = (val: number | null | undefined) => val ?? 0;
    const safeCategoryDisplayValue = (val: number | null | undefined) => {
      const actualValue = val ?? 0;
      return actualValue === 0 ? 0.3 : actualValue;
    };

    return CATEGORY_METADATA.map(category => {
      const topScore = safeCategoryValue(metricData[category.key]?.benchmark);
      const avgScore = safeCategoryValue(metricData[category.key]?.average);
      const currentScore = safeCategoryValue(metricData[category.key]?.current);

      return {
        category: category.name,
        icon: category.icon,
        color: category.color,
        'Top Scoring Hotel': topScore,
        'Top Scoring Hotel Display': safeCategoryDisplayValue(
          metricData[category.key]?.benchmark
        ),
        topHotelName:
          metricData[category.key]?.topHotelName || 'Top Scoring Hotel',
        Average: avgScore,
        'Average Display': safeCategoryDisplayValue(
          metricData[category.key]?.average
        ),
        [hotelName]: currentScore,
        [`${hotelName} Display`]: safeCategoryDisplayValue(
          metricData[category.key]?.current
        ),
      };
    });
  }, [
    selectedMetric,
    selectedBrandIndex,
    getMetricData,
    hotel?.hotel_location_name,
  ]);

  // Custom tooltip for horizontal bar chart
  const CustomHorizontalTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const IconComponent = payload[0]?.payload?.icon;
      const hotelName = hotel?.hotel_location_name || 'My Hotel';

      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            {IconComponent && (
              <div className="w-5 h-5 flex items-center justify-center">
                <IconComponent color={payload[0]?.payload?.color} />
              </div>
            )}
            <p className="text-sm font-semibold text-gray-800">{label}</p>
          </div>
          {payload.map((entry: any, index: number) => {
            // Map display name back to actual value key
            let actualValueKey = entry.name;
            if (entry.name === 'Top Scoring Hotel Display') {
              actualValueKey = 'Top Scoring Hotel';
            } else if (entry.name === 'Average Display') {
              actualValueKey = 'Average';
            } else if (entry.name === `${hotelName} Display`) {
              actualValueKey = hotelName;
            }

            const actualValue = entry.payload[actualValueKey];

            return (
              <div key={index} className="flex items-center gap-2 mb-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-600">
                  {actualValueKey}: {actualValue}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Calculate statistics
  const { totalScore, variance, gapToBenchmark, averageScore, benchmarkScore } =
    useMemo(() => {
      if (!getMetricData)
        return {
          totalScore: 0,
          variance: 0,
          gapToBenchmark: 0,
          averageScore: 0,
          benchmarkScore: 0,
        };

      let aloscore;
      if (
        selectedMetric === 'brandWise' &&
        getMetricData.brandWise?.[selectedBrandIndex]
      ) {
        aloscore = getMetricData.brandWise[selectedBrandIndex].data.alo_score;
      } else {
        aloscore = getMetricData[selectedMetric]?.alo_score;
      }

      if (!aloscore)
        return {
          totalScore: 0,
          variance: 0,
          gapToBenchmark: 0,
          averageScore: 0,
          benchmarkScore: 0,
        };

      // Safely handle null values
      const current = aloscore.current ?? 0;
      const average = aloscore.average ?? 0;
      const benchmark = aloscore.benchmark ?? 0;
      const variance = aloscore.variance ?? current - average;

      return {
        totalScore: current,
        variance: variance,
        gapToBenchmark: benchmark - current,
        averageScore: average,
        benchmarkScore: benchmark,
      };
    }, [selectedMetric, selectedBrandIndex, getMetricData]);

  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const actualValue = payload[0].payload.value;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-semibold text-gray-800">
            {payload[0].payload.name}
          </p>
          <p className="text-sm text-gray-600">Score: {actualValue}</p>
        </div>
      );
    }
    return null;
  };

  // Custom X-axis tick for bar chart to handle long hotel names
  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const text = payload.value;
    // Don't truncate standard labels like "Top Scoring Hotel" and "Average"
    const shouldTruncate = text !== 'Top Scoring Hotel' && text !== 'Average';
    const displayText = shouldTruncate ? truncateText(text, 15) : text;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#5e718d"
          fontSize={14}
        >
          {displayText}
        </text>
      </g>
    );
  };

  // Custom Y-axis tick with text wrapping and icons
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const words = payload.value.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    // Group words into lines (max 3 words per line for readability)
    words.forEach((word: string, index: number) => {
      if (currentLine === '') {
        currentLine = word;
      } else if (currentLine.split(' ').length < 3) {
        currentLine += ` ${word}`;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) {
      lines.push(currentLine);
    }

    // Find the icon for this category
    const categoryData = categoryComparisonData.find(
      cat => cat.category === payload.value
    );
    const IconComponent = categoryData?.icon;

    return (
      <g transform={`translate(${x},${y})`}>
        {IconComponent && (
          <foreignObject x={-24} y={-16} width={20} height={28}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconComponent color={categoryData?.color} />
            </div>
          </foreignObject>
        )}
        <text
          x={-30}
          y={0}
          dy={lines.length > 1 ? -8 : 4}
          textAnchor="end"
          fill="#5e718d"
          fontSize={16}
        >
          {lines.map((line, index) => (
            <tspan key={index} x={-30} dy={index === 0 ? 0 : 18}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  // Check if data is available for the selected metric
  const hasData = useMemo(() => {
    if (!getMetricData) return false;

    let aloscore;
    if (selectedMetric === 'brandWise') {
      aloscore = getMetricData.brandWise?.[selectedBrandIndex]?.data?.alo_score;
    } else {
      aloscore = getMetricData[selectedMetric]?.alo_score;
    }

    // Check if alo_score exists and has valid non-null values
    return (
      aloscore != null &&
      (aloscore.current != null ||
        aloscore.average != null ||
        aloscore.benchmark != null)
    );
  }, [getMetricData, selectedMetric, selectedBrandIndex]);
  if (loading) {
    return (
      <Loader message="Loading Market Intelligence Reports for the hotel...." />
    );
  }

  const handleDownloadReport = async () => {
    if (!hotel?.id) {
      error('Hotel information not available');
      return;
    }

    setIsDownloading(true);
    try {
      // Determine brandId if brandWise is selected
      const brandId =
        selectedMetric === 'brandWise' &&
        getMetricData?.brandWise?.[selectedBrandIndex]
          ? getMetricData.brandWise[selectedBrandIndex].brandId
          : undefined;

      // Map view parameter values
      const viewParamMap: Record<string, string> = {
        overall: 'overall',
        marketWise: 'market',
        brandWise: 'brand',
      };

      // Build query parameters
      const params = new URLSearchParams();
      params.append('view', viewParamMap[selectedMetric]);
      if (brandId) {
        params.append('brandId', brandId);
      }

      const endpoint = `${API_ENDPOINTS.hotels.downloadBenchmarkReport(hotel.id)}?${params.toString()}`;
      const filename = `Market_Intelligence_Report_${viewParamMap[selectedMetric]}_${new Date().toISOString().split('T')[0]}.pdf`;

      await downloadPDF(endpoint, filename);
      success('Report downloaded successfully!');
    } catch (err: any) {
      console.error('Failed to download report:', err);
      error(err.message || 'Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Methodology content component
  const methodologyContent = (
    <div className="space-y-4 text-sm text-gray-700">
      <div>
        <h4 className="font-semibold text-gray-900 mb-2">
          Comparative Cohorts
        </h4>
        <ul className="space-y-2 ml-4">
          <li>
            <span className="font-medium">Global (Overall Index):</span> A
            comprehensive comparison against every hotel within the Alō Index,
            providing a high-level view of where you stand in the global market.
          </li>
          <li>
            <span className="font-medium">Market-Specific:</span> A localized
            top score that compares your performance against hotels within your
            specific geographic market, accounting for local sustainability
            trends.
          </li>
          <li>
            <span className="font-medium">Brand Performance:</span> A targeted
            look at how your property compares to others within your specific
            hotel brand, helping you track alignment with corporate
            sustainability goals.
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-2">
          Data Integrity & Minimum Thresholds
        </h4>
        <p className="ml-4">
          To ensure statistical validity and maintain the confidentiality of our
          partner hotels, we apply a Minimum Threshold of 3 hotels per cohort.
          Market Intelligence data will only be displayed when a cohort contains
          at least three properties, ensuring your competitive intelligence is
          both accurate and anonymous.
        </p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-2">
          The Alō Scoring Methodology
        </h4>
        <p className="ml-4">
          Market Intelligence built upon a trusted global framework that
          evaluates 100+ standardized data points across Planet, People, and
          Policy. All results are normalized on a clear 0-100 scale to simplify
          performance tracking:
        </p>
        <ul className="space-y-1 ml-8 mt-2">
          <li>
            •{' '}
            <span className="font-medium">Top Scoring Hotel (The Leader)</span>{' '}
            represents the highest-performing hotel within your specific cohort.
            Use this as your target for "best-in-class" performance.
          </li>
          <li>
            • <span className="font-medium">Average</span> shows the mean score
            across all hotels in the cohort, serving as the baseline for
            standard industry performance.
          </li>
          <li>
            • <span className="font-medium">Your Score</span> indicates your
            property&apos;s current verified performance rating. This score
            reflects your latest submission, including credits for third-party
            certifications and verified sustainability initiatives.
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="w-full h-full">
      {/* Header with Dropdown */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          Market Intelligence
        </h2>
        <div className="flex gap-3">
          <Button
            onClick={handleDownloadReport}
            disabled={isDownloading || !hasData}
          >
            {isDownloading ? 'Downloading...' : 'Download Report'}
          </Button>
          {selectedMetric === 'brandWise' && brandOptions.length > 0 && (
            <UiTooltip
              content="Select a brand"
              icon={
                <SelectDropdown
                  options={brandOptions}
                  value={selectedBrandIndex.toString()}
                  onChange={value => setSelectedBrandIndex(parseInt(value))}
                  variant="transparent"
                  className="bg-white w-48"
                  // borderColor="var(--color-primary-500)"
                />
              }
            />
          )}

          <UiTooltip
            content="Select a metric"
            icon={
              <SelectDropdown
                options={metricOptions}
                value={selectedMetric}
                onChange={value =>
                  setSelectedMetric(
                    value as 'overall' | 'marketWise' | 'brandWise'
                  )
                }
                variant="transparent"
                className="bg-white w-48"
              />
            }
          />
        </div>
      </div>

      {/* Methodology Info Banner */}
      <div className="mb-6">
        <Banner
          titleBadgeText="Methodology"
          headerComponent={
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Understanding Your Performance
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Learn how our comparative intelligence helps you evaluate your
                sustainability progress and communicate your results with
                confidence.
              </p>
            </div>
          }
          contentComponent={methodologyContent}
          expandButtonText="Show"
          collapseButtonText="Hide"
          initialExpanded={false}
          isAccordion={true}
          containerStyles={{
            $backgroundColor: '#EEF2FF',
            $borderColor: '#818CF8',
          }}
          badgeStyles={{
            $badgeBackgroundColor: '#818CF8',
            $badgeTextColor: '#FFFFFF',
          }}
        />
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm">
          <div className="text-center max-w-md">
            <div className="mb-6">
              <svg
                className="mx-auto h-24 w-24 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Market Intelligence Data Available
            </h3>
            <p className="text-gray-500 mb-4">
              {selectedMetric === 'overall' &&
                'Overall Market Intelligence data is not available for this hotel.'}
              {selectedMetric === 'marketWise' &&
                'Market-wise Market Intelligence data is not available for this hotel.'}
              {selectedMetric === 'brandWise' &&
                'Brand-wise Market Intelligence data is not available for the selected brand.'}
            </p>
            <p className="text-sm text-gray-400">
              Please select a different metric or contact support for more
              information.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Top Statistics Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left: Statistics */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h1 className="text-lg font-semibold text-gray-900">
                Global Analysis
              </h1>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between lg:flex-col xl:flex-row xl:items-center xl:justify-between gap-6 md:gap-8">
                <div className="flex-1 flex flex-col space-y-6 lg:flex-row lg:space-x-6 lg:space-y-0 xl:flex-col xl:space-y-6 xl:space-x-0">
                  {/* Variance from Average */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-xl lg:text-xl xl:text-2xl 2xl:text-2xl">
                        📊
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs lg:text-sm xl:text-sm 2xl:text-sm font-medium text-gray-500 mb-1">
                        Gap from Average
                      </p>
                      <div className="flex items-baseline gap-2 mb-1">
                        <p
                          className={`text-2xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold ${variance >= 0 ? 'text-success' : 'text-error'}`}
                        >
                          {variance >= 0 ? '+' : ''}
                          {variance}
                        </p>
                        <span className="text-gray-600 text-sm lg:text-sm xl:text-base 2xl:text-base font-medium">
                          pts
                        </span>
                      </div>
                      <p className="text-xs lg:text-sm xl:text-sm 2xl:text-sm text-gray-500">
                        vs global avg{' '}
                        <span className="font-semibold">({averageScore})</span>
                      </p>
                    </div>
                  </div>

                  {/* Gap to Benchmark */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-xl lg:text-xl xl:text-2xl 2xl:text-2xl">
                        🎯
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs lg:text-sm xl:text-sm 2xl:text-sm font-medium text-gray-500 mb-1">
                        Gap to Top Scoring Hotel
                      </p>
                      <div className="flex items-baseline gap-2 mb-1">
                        <p className="text-2xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold text-gray-900">
                          {gapToBenchmark}
                        </p>
                        <span className="text-gray-600 text-sm lg:text-sm xl:text-base 2xl:text-base font-medium">
                          pts away
                        </span>
                      </div>
                      <p className="text-xs lg:text-sm xl:text-sm 2xl:text-sm text-gray-500">
                        <span className="font-semibold text-primary-600">
                          {benchmarkScore > 0
                            ? Math.round((totalScore / benchmarkScore) * 100)
                            : 0}
                          %
                        </span>{' '}
                        of target ({benchmarkScore})
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0 flex justify-center lg:justify-start xl:justify-center">
                  <ESGScoreDisplay
                    scoreE={hotel?.score_e || null}
                    scoreS={hotel?.score_s || null}
                    scoreG={hotel?.score_g || null}
                    scoreAlo={hotel?.score_alo || null}
                    className="w-44 md:w-44 lg:w-52 xl:w-52 2xl:w-72"
                  />
                </div>
              </div>
            </div>

            {/* Right: Overall Total Score Bar Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Alō Score
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Comparison against global average and top scoring hotel
                </p>
              </div>

              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={overallScoreData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9eff6"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={<CustomXAxisTick />}
                    interval={0}
                    axisLine={{ stroke: '#d7dfe9' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: '#5e718d', fontSize: 11 }}
                    axisLine={{ stroke: '#d7dfe9' }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(36, 151, 235, 0.1)' }}
                  />
                  <Bar
                    dataKey="displayValue"
                    radius={[8, 8, 0, 0]}
                    barSize={50}
                  >
                    {overallScoreData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="flex justify-center gap-6 mt-2">
                {overallScoreData.map(item => (
                  <UiTooltip
                    key={item.name}
                    content={item.name}
                    icon={
                      <div className="flex items-center gap-2 cursor-default">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-xs font-medium text-gray-700">
                          {truncateText(item.name, 20)}
                        </span>
                      </div>
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Category Breakdown - Full Width */}
          <div className="bg-white rounded-xl p-2 shadow-sm">
            <div className="mb-4 px-4 mt-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Category Breakdown
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Category-based performance comparison against peer average and
                top-scoring hotel
              </p>
            </div>

            {/* Legend */}
            <div className="flex justify-start gap-8 mb-4 px-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: Colors['Top Scoring Hotel'] }}
                />
                <span className="text-sm font-medium text-gray-700">
                  Top Scoring Hotel
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: Colors['Average'] }}
                />
                <span className="text-sm font-medium text-gray-700">
                  Average
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: Colors['My Hotel'] }}
                />
                <span className="text-sm font-medium text-gray-700">
                  {hotel?.hotel_location_name || 'My Hotel'}
                </span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={1000}>
              <BarChart
                data={categoryComparisonData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: -20, bottom: 10 }}
                barCategoryGap="30%"
                barGap={5}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e9eff6"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fill: '#5e718d', fontSize: 14 }}
                />
                <YAxis
                  type="category"
                  dataKey="category"
                  tick={<CustomYAxisTick />}
                  width={250}
                />
                <Tooltip
                  content={<CustomHorizontalTooltip />}
                  cursor={{ fill: 'rgba(36, 151, 235, 0.1)' }}
                />
                <Bar
                  dataKey="Top Scoring Hotel Display"
                  fill={Colors['Top Scoring Hotel']}
                  radius={[0, 4, 4, 0]}
                  barSize={12}
                />
                <Bar
                  dataKey="Average Display"
                  fill={Colors['Average']}
                  radius={[0, 4, 4, 0]}
                  barSize={12}
                />
                <Bar
                  dataKey={`${hotel?.hotel_location_name || 'My Hotel'} Display`}
                  fill={Colors['My Hotel']}
                  radius={[0, 4, 4, 0]}
                  barSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
